import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Car, Plus, ShieldCheck, Fuel } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import RowActions from "@/components/RowActions";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const EMPTY = {
    name: "", kind: "Car", make: "", model: "", year: "", reg_number: "",
    fuel_type: "Petrol", color: "", owner: "Self", location: "",
    purchase_date: "", purchase_price: "", currency: "INR",
    insurance_provider: "", insurance_policy_no: "", insurance_end_date: "",
    puc_end_date: "", fastag_id: "", fastag_end_date: "", rc_end_date: "",
    notes: "",
};

function daysToExpiry(iso) {
    if (!iso) return null;
    const d = new Date(iso + "T00:00:00");
    return Math.round((d - new Date()) / 86400000);
}
function expiryChip(days) {
    if (days == null) return null;
    if (days < 0) return <span className="text-[10px] px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-red-700 uppercase tracking-widest">{Math.abs(days)}d overdue</span>;
    if (days <= 7) return <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 uppercase tracking-widest">in {days}d</span>;
    if (days <= 30) return <span className="text-[10px] px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 uppercase tracking-widest">in {days}d</span>;
    return <span className="text-[10px] px-2 py-0.5 rounded-full border border-stone-200 bg-stone-50 text-stone-600 uppercase tracking-widest">in {days}d</span>;
}

export default function Vehicles() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ types: [], fuels: [] });
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/vehicles");
            setRows(data || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => {
        api.get("/vehicles/meta/enums").then((r) => setMeta(r.data || { types: [], fuels: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            ["year", "purchase_price"].forEach((k) => { if (body[k] === "" || body[k] == null) delete body[k]; else body[k] = Number(body[k]); });
            ["purchase_date", "insurance_end_date", "puc_end_date", "fastag_end_date", "rc_end_date"].forEach((k) => { if (!body[k]) delete body[k]; });
            if (editingId) {
                await api.patch(`/vehicles/${editingId}`, body);
                toast.success("Vehicle updated");
            } else {
                await api.post("/vehicles", body);
                toast.success("Vehicle added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (v) => {
        setEditingId(v.id);
        setF({
            name: v.name || "", kind: v.kind || "Car", make: v.make || "", model: v.model || "",
            year: v.year ?? "", reg_number: v.reg_number || "",
            fuel_type: v.fuel_type || "Petrol", color: v.color || "",
            owner: v.owner || "Self", location: v.location || "",
            purchase_date: (v.purchase_date || "").slice(0, 10),
            purchase_price: v.purchase_price ?? "", currency: v.currency || "INR",
            insurance_provider: v.insurance_provider || "",
            insurance_policy_no: v.insurance_policy_no || "",
            insurance_end_date: (v.insurance_end_date || "").slice(0, 10),
            puc_end_date: (v.puc_end_date || "").slice(0, 10),
            fastag_id: v.fastag_id || "",
            fastag_end_date: (v.fastag_end_date || "").slice(0, 10),
            rc_end_date: (v.rc_end_date || "").slice(0, 10),
            notes: v.notes || "",
        });
        setOpen(true);
    };

    const remove = async (v) => {
        if (!window.confirm(`Delete vehicle "${v.name}"? This cannot be undone.`)) return;
        try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="vehicles-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Car size={12} /> Vehicle Hub</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Every vehicle you drive.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Cars, bikes and scooters &mdash; with insurance, PUC, FASTag and RC expiries automatically
                        flowing into the Renewals tracker, plus a fuel / service / challan log.
                    </p>
                </div>
                {canEdit && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="veh-add" onClick={openAdd}>
                                <Plus size={14} className="mr-1.5" /> Add vehicle
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="veh-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">{editingId ? "Edit vehicle" : "New vehicle"}</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    Expiries you set (insurance / PUC / FASTag / RC) will show on Renewals automatically.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div><Label className="eyebrow">Name *</Label>
                                        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" placeholder="e.g. My Innova" data-testid="veh-name" /></div>
                                    <div><Label className="eyebrow">Type</Label>
                                        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.types.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Make</Label><Input value={f.make} onChange={(e) => setF({ ...f, make: e.target.value })} className="mt-1.5" placeholder="Toyota" /></div>
                                    <div><Label className="eyebrow">Model</Label><Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Year</Label><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                    <div><Label className="eyebrow">Reg number</Label>
                                        <Input value={f.reg_number} onChange={(e) => setF({ ...f, reg_number: e.target.value.toUpperCase() })} className="mt-1.5 font-mono-figure uppercase" placeholder="KA 01 AB 1234" data-testid="veh-reg" /></div>
                                    <div><Label className="eyebrow">Fuel type</Label>
                                        <Select value={f.fuel_type} onValueChange={(v) => setF({ ...f, fuel_type: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.fuels.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Color</Label><Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Purchase date</Label><Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Purchase price</Label><Input type="number" step="0.01" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-stone-100">
                                    <div className="md:col-span-2 text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
                                        <ShieldCheck size={11} /> Renewables (auto-mirror to Renewals)
                                    </div>
                                    <div><Label className="eyebrow">Insurance provider</Label><Input value={f.insurance_provider} onChange={(e) => setF({ ...f, insurance_provider: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Insurance ends</Label><Input type="date" value={f.insurance_end_date} onChange={(e) => setF({ ...f, insurance_end_date: e.target.value })} className="mt-1.5" data-testid="veh-ins-end" /></div>
                                    <div><Label className="eyebrow">PUC ends</Label><Input type="date" value={f.puc_end_date} onChange={(e) => setF({ ...f, puc_end_date: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">FASTag ends</Label><Input type="date" value={f.fastag_end_date} onChange={(e) => setF({ ...f, fastag_end_date: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">FASTag ID</Label><Input value={f.fastag_id} onChange={(e) => setF({ ...f, fastag_id: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                    <div><Label className="eyebrow">RC ends</Label><Input type="date" value={f.rc_end_date} onChange={(e) => setF({ ...f, rc_end_date: e.target.value })} className="mt-1.5" /></div>
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="veh-submit">
                                    {busy ? "Saving…" : editingId ? "Save changes" : "Add vehicle"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {rows.length === 0 && (
                    <div className="col-span-full rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center text-stone-500 text-sm" data-testid="veh-empty">
                        <Car size={20} className="mx-auto mb-2 text-stone-400" />
                        No vehicles yet. Add one to start tracking insurance, PUC, FASTag and RC.
                    </div>
                )}
                {rows.map((v) => (
                    <div key={v.id} className="relative rounded-lg border border-stone-200 bg-white p-5 hover:border-stone-300 transition-colors" data-testid={`veh-card-${v.id}`}>
                        {canEdit && (
                            <div className="absolute top-3 right-3 z-10" onClick={(e) => e.preventDefault()}>
                                <RowActions
                                    id={v.id}
                                    testIdBase="veh"
                                    onEdit={() => openEdit(v)}
                                    onDelete={() => remove(v)}
                                    compact
                                />
                            </div>
                        )}
                        <Link to={`/vehicles/${v.id}`} className="block">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-stone-900 truncate">{v.name}</div>
                                <div className="text-[11px] text-stone-500 mt-0.5">
                                    {[v.make, v.model, v.year].filter(Boolean).join(" · ") || "—"}
                                </div>
                                {v.reg_number && <div className="text-[11px] font-mono-figure text-stone-700 mt-1">{v.reg_number}</div>}
                            </div>
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-stone-200 bg-stone-50 text-stone-600 flex items-center gap-1">
                                <Fuel size={9} /> {v.fuel_type || "—"}
                            </span>
                        </div>
                        <div className="mt-4 space-y-1.5 text-[12px]">
                            {[
                                ["Insurance", v.insurance_end_date],
                                ["PUC", v.puc_end_date],
                                ["FASTag", v.fastag_end_date],
                                ["RC", v.rc_end_date],
                            ].map(([lbl, val]) => (
                                <div key={lbl} className="flex items-center justify-between gap-2">
                                    <div className="text-stone-500">{lbl}</div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-stone-700">{fmtDate(val) || <span className="text-stone-400">—</span>}</span>
                                        {expiryChip(daysToExpiry(val))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}
